// CoachMark — verankerte Sprechblase der geführten Einmal-Hinweise im
// TTS-Flow (lib/coachMarks.ts). Findet ihre Ziele über `data-coach="…"` im
// DOM, statt Refs durch fünf Komponenten zu reichen, und misst sie bei
// Scroll (Capture — der Content-Pane scrollt, nicht das Fenster), Resize und
// Layout-Änderungen neu.
//
// Befestigung (`attach`, Default `auto`) — damit nichts pro Frame nachgeführt
// werden muss (eine reine position:fixed-Fassung hing beim Scrollen einen
// Frame hinterher, „es wackelt", Philip 2026-10-03):
//   • Ziel scrollt mit dem Inhalt → Portal IN den Scroll-Container (AppShell
//     .contentScroll, position:relative) mit `position:absolute` in
//     Inhaltskoordinaten; die Blase scrollt nativ mit. z-index unter dem
//     sticky Timeline-Band, damit sie wie jeder Inhalt darunter verschwindet.
//   • Ziel ist sticky und haftet gerade (Band oben, BottomBar unten) → Portal
//     in <body>, `position:fixed`. `auto` erkennt das pro Messung; vor dem
//     Haften scrollt auch ein sticky Ziel mit dem Inhalt.
//
// Kollision mit dem sticky Band (`avoid`): Würde eine mitscrollende Blase
// über ihrem Ziel in das Band ragen, klappt sie unter das Ziel.
//
// Zwei Formen:
//   • Caret-Form (Standard): Blase über oder unter dem ersten Ziel, kleine
//     Spitze zeigt auf dessen Mitte. Gleiche Familie wie der Stern-Hinweis
//     am TakeChip und der ScrollHint (Brandfarbe, Bob-Animation).
//   • Pfeil-Form (`arrows`): Blase über (oder unter) den Zielen, dazu eine
//     gebogene SVG-Linie mit Pfeilspitze zu jedem Ziel (Philip: „ein popup
//     mit gebogenen pfeilen, zeigt auf generate all oder generate an einem
//     block, gleichzeitig 2 pfeile").
//
// Ziele ohne Ausdehnung (display:none, z. B. die ParamPanel-Kopie im
// geschlossenen Mobile-Sheet) werden ignoriert.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { scrollParentOf } from '@/lib/scrollParent';
import styles from './CoachMark.module.css';

interface Props {
  /** `data-coach`-Werte der Ziele; das erste ist der Anker der Blase. */
  targets: string[];
  /** Text der Blase (bei `variant="arrow"` entfällt er). */
  text?: string;
  /** `bubble` (Standard) oder `arrow`: nur ein wippender Pfeil ohne Text und
   *  ohne ×, z. B. „hier geht's weiter" auf das nächste Asset in der Sidebar. */
  variant?: 'bubble' | 'arrow';
  /** `right` nur für `variant="arrow"`: Pfeil rechts neben dem Ziel, zeigt nach links. */
  placement?: 'above' | 'below' | 'right';
  /** Gebogene Pfeile zu allen Zielen statt der Caret-Spitze. */
  arrows?: boolean;
  /** `auto` = fixed nur, wenn der Anker (oder ein sticky Vorfahr) gerade
   *  haftet; `scroll`/`fixed` erzwingen eine Befestigung. */
  attach?: 'auto' | 'scroll' | 'fixed';
  /** `data-coach`-Wert eines sticky Elements, dem eine mitscrollende Blase
   *  ausweicht (klappt unter ihr Ziel, statt hineinzuragen). */
  avoid?: string;
  /** Klick auf eines der Ziele schließt den Hinweis (Philip 2026-10-03: der
   *  Generate-Hinweis soll beim Klick verschwinden, nicht erst beim Take). */
  dismissOnTargetClick?: boolean;
  /** Irgendein Klick irgendwo schließt den Hinweis (Pfeil aufs nächste Asset). */
  dismissOnAnyClick?: boolean;
  onDismiss(): void;
}

interface Rect { left: number; top: number; width: number; height: number }
interface Pt { x: number; y: number }

interface Layout {
  mode: 'fixed' | 'scroll';
  placement: 'above' | 'below' | 'right';
  bubble: { left: number; top: number };
  caretX: number;
  visible: boolean;
  arrows: Array<{ from: Pt; to: Pt }>;
  /** Größe der Pfeil-Fläche (Viewport bzw. Scroll-Inhalt). */
  canvas: { width: number; height: number };
}

const GAP = 10;
const ARROW_GAP = 26; // Pfeile brauchen Platz zum Herauslaufen
const EDGE = 8;
const STUCK_EPS = 1.5;

function visibleTargets(ids: string[]): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const id of ids) {
    const els = document.querySelectorAll<HTMLElement>(`[data-coach="${id}"]`);
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) { out.push(el); break; } // erste sichtbare Instanz je id
    }
  }
  return out;
}

/** Haftet das Element (oder ein sticky Vorfahr innerhalb des Scroll-
 *  Containers) gerade am oberen oder unteren Rand? */
function isStuck(el: HTMLElement, scroller: HTMLElement): boolean {
  const sRect = scroller.getBoundingClientRect();
  let cur: HTMLElement | null = el;
  while (cur && cur !== scroller) {
    if (getComputedStyle(cur).position === 'sticky') {
      const r = cur.getBoundingClientRect();
      return r.top <= sRect.top + STUCK_EPS || r.bottom >= sRect.bottom - STUCK_EPS;
    }
    cur = cur.parentElement;
  }
  return false;
}

/** Gebogener Pfad (kubische Bézier) von `from` nach `to`. Die Kontrollpunkte
 *  ziehen die Linie erst senkrecht aus der Blase heraus und lassen sie dann
 *  seitlich ins Ziel einschwenken. */
export function curvedPath(from: Pt, to: Pt): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const c1 = { x: from.x, y: from.y + dy * 0.55 };
  const c2 = { x: to.x - dx * 0.25, y: to.y - dy * 0.45 };
  return `M ${from.x} ${from.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${to.x} ${to.y}`;
}

function sameLayout(a: Layout | null, b: Layout): boolean {
  if (!a) return false;
  if (a.mode !== b.mode || a.placement !== b.placement || a.visible !== b.visible || a.caretX !== b.caretX) return false;
  if (a.bubble.left !== b.bubble.left || a.bubble.top !== b.bubble.top) return false;
  if (a.canvas.width !== b.canvas.width || a.canvas.height !== b.canvas.height) return false;
  if (a.arrows.length !== b.arrows.length) return false;
  return a.arrows.every((x, i) => {
    const y = b.arrows[i];
    return x.from.x === y.from.x && x.from.y === y.from.y && x.to.x === y.to.x && x.to.y === y.to.y;
  });
}

export function CoachMark({
  targets, text, variant = 'bubble', placement = 'above', arrows = false, attach = 'auto', avoid,
  dismissOnTargetClick = false, dismissOnAnyClick = false, onDismiss,
}: Props) {
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  // Ziele und Callback über Refs lesen: TTSView reicht bei jedem Render ein
  // neues Array herein — als Effekt-Abhängigkeit hätte das die Klick-Karenz
  // bei jedem Re-Render neu gestartet (der Upload-Hinweis blieb nach dem
  // Klick stehen, Sichtprüfung 2026-10-03).
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const targetsKey = targets.join('|');
  // Portal-Ziel: Scroll-Container des Ankers (scroll) oder <body> (fixed).
  const [host, setHost] = useState<HTMLElement>(() => document.body);

  const measure = useCallback(() => {
    const els = visibleTargets(targetsRef.current);
    const bubble = bubbleRef.current;
    if (els.length === 0 || !bubble) {
      setLayout((l) => (l && !l.visible ? l : {
        mode: 'fixed', placement, bubble: { left: 0, top: 0 }, caretX: 0, visible: false, arrows: [], canvas: { width: 0, height: 0 },
      }));
      return;
    }
    const scroller = attach === 'fixed' ? null : scrollParentOf(els[0]);
    const useFixed = attach === 'fixed' || !scroller || (attach === 'auto' && isStuck(els[0], scroller));
    const mode: Layout['mode'] = useFixed ? 'fixed' : 'scroll';
    const wantHost = mode === 'scroll' && scroller ? scroller : document.body;
    if (wantHost !== host) {
      // Portal wechselt → Blase wird neu gemountet, der Layout-Effekt misst
      // dann mit dem neuen Element nach.
      setHost(wantHost);
      return;
    }

    // Alle Maße in den Koordinaten des Portal-Ziels: Viewport (fixed) oder
    // Scroll-Inhalt (absolute im Scroll-Container).
    const sRect = mode === 'scroll' && scroller ? scroller.getBoundingClientRect() : null;
    const ox = sRect && scroller ? -sRect.left + scroller.scrollLeft : 0;
    const oy = sRect && scroller ? -sRect.top + scroller.scrollTop : 0;
    const toLocal = (r: DOMRect): Rect => ({ left: r.left + ox, top: r.top + oy, width: r.width, height: r.height });
    const rects = els.map((el) => toLocal(el.getBoundingClientRect()));
    const canvas = sRect && scroller
      ? { width: scroller.scrollWidth, height: scroller.scrollHeight }
      : { width: window.innerWidth, height: window.innerHeight };
    const maxX = sRect && scroller ? scroller.clientWidth : window.innerWidth;

    const bw = bubble.offsetWidth;
    const bh = bubble.offsetHeight;
    const anchor = rects[0];
    const centerX = arrows
      ? rects.reduce((n, r) => n + r.left + r.width / 2, 0) / rects.length
      : anchor.left + anchor.width / 2;
    const refTop = arrows ? Math.min(...rects.map((r) => r.top)) : anchor.top;
    const refBottom = anchor.top + anchor.height;
    const gapAbove = arrows ? ARROW_GAP : GAP;
    const gapBelow = arrows ? ARROW_GAP : GAP;

    // Platzierung: gewünscht `placement`; eine mitscrollende Blase, die oben
    // in das sticky Band (`avoid`) ragen würde, klappt unter ihr Ziel.
    let eff: Layout['placement'] = placement;
    let top = eff === 'above' ? refTop - bh - gapAbove : eff === 'below' ? refBottom + gapBelow : anchor.top + anchor.height / 2 - bh / 2;
    if (mode === 'scroll' && eff === 'above' && avoid) {
      const avoidEl = document.querySelector<HTMLElement>(`[data-coach="${avoid}"]`);
      if (avoidEl) {
        const aRect = toLocal(avoidEl.getBoundingClientRect());
        if (top < aRect.top + aRect.height + 4) {
          eff = 'below';
          top = refBottom + gapBelow;
        }
      }
    }
    if (mode === 'fixed') top = Math.max(EDGE, Math.min(window.innerHeight - bh - EDGE, top));
    else top = Math.max(EDGE, top);
    top = Math.round(top);
    const left = eff === 'right'
      ? Math.round(anchor.left + anchor.width + GAP)
      : Math.round(Math.max(EDGE, Math.min(maxX - bw - EDGE, centerX - bw / 2)));
    const caretX = Math.round(Math.max(14, Math.min(bw - 14, centerX - left)));

    // `fixed`: nur sichtbar, wenn der Anker im Fenster liegt. `scroll`: die
    // Blase scrollt mit dem Anker, nichts zu prüfen.
    const anchorVisible = mode === 'scroll'
      ? true
      : anchor.top < window.innerHeight - 8 && anchor.top + anchor.height > 8;

    // Pfeile starten an der Blasenkante, die dem jeweiligen Ziel zugewandt ist.
    const fromTop = { x: left + bw / 2, y: top };
    const fromBottom = { x: left + bw / 2, y: top + bh };
    const arrowList = arrows
      ? rects.map((r) => {
          const targetAbove = r.top + r.height <= top;
          return {
            from: targetAbove ? fromTop : fromBottom,
            to: { x: Math.round(r.left + r.width / 2), y: Math.round(targetAbove ? r.top + r.height + 2 : r.top - 2) },
          };
        })
      : [];

    const next: Layout = { mode, placement: eff, bubble: { left, top }, caretX, visible: anchorVisible, arrows: arrowList, canvas };
    setLayout((l) => (sameLayout(l, next) ? l : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- targetsKey vertritt targets (Ref)
  }, [targetsKey, placement, arrows, attach, avoid, host]);

  // Erste Messung nach dem Mount (Blasenbreite steht erst dann fest), dann
  // auf Scroll (Capture), Resize, Layout-Änderungen im Scroll-Inhalt und
  // Klicks (ParamPanel wandert beim Blockwechsel).
  useLayoutEffect(() => { measure(); }, [measure, text, host]);
  useEffect(() => {
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => { raf = 0; measure(); });
    };
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    document.addEventListener('click', schedule, true);
    document.addEventListener('transitionend', schedule, true);
    const ro = new ResizeObserver(schedule);
    ro.observe(document.body);
    // Inhaltsgröße des Scroll-Containers: dessen direkte Kinder beobachten
    // (<main> wächst, wenn Blöcke dazukommen).
    if (host !== document.body) for (const c of host.children) ro.observe(c);
    const t = window.setTimeout(schedule, 120);
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('click', schedule, true);
      document.removeEventListener('transitionend', schedule, true);
      ro.disconnect();
      window.clearTimeout(t);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [measure, host]);

  // Klick-Dismiss: auf ein Ziel (Generate-Buttons, Auto-Split, Upload) oder
  // irgendwo (Pfeil aufs nächste Asset). Capture-Phase, damit auch Klicks
  // zählen, die ein Handler per stopPropagation schluckt; kurze Karenz,
  // damit der Klick, der den Hinweis gerade erst geöffnet hat, ihn nicht
  // sofort wieder schließt.
  useEffect(() => {
    if (!dismissOnTargetClick && !dismissOnAnyClick) return;
    let armed = false;
    const arm = window.setTimeout(() => { armed = true; }, 300);
    const onClick = (e: MouseEvent) => {
      if (!armed) return;
      const t = e.target as Node | null;
      if (!t) return;
      if (bubbleRef.current?.contains(t)) return; // × hat seinen eigenen Handler
      if (dismissOnAnyClick) { onDismissRef.current(); return; }
      if (visibleTargets(targetsRef.current).some((el) => el.contains(t))) onDismissRef.current();
    };
    document.addEventListener('click', onClick, true);
    return () => { window.clearTimeout(arm); document.removeEventListener('click', onClick, true); };
  }, [dismissOnTargetClick, dismissOnAnyClick]);

  const inScroller = host !== document.body;
  const eff = layout?.placement ?? placement;
  const isArrow = variant === 'arrow';
  const cls = [
    isArrow ? styles.arrowOnly : styles.bubble,
    inScroller ? styles.inScroller : styles.inViewport,
    eff === 'above' ? styles.above : eff === 'below' ? styles.below : '',
    arrows || isArrow ? styles.noCaret : '',
    layout?.visible ? styles.visible : '',
  ].filter(Boolean).join(' ');

  const node = (
    <>
      <div
        ref={bubbleRef}
        className={cls}
        role="note"
        style={layout ? { left: layout.bubble.left, top: layout.bubble.top, ['--caret-x' as string]: `${layout.caretX}px` } : undefined}
      >
        {isArrow ? (
          <i className={`ph-bold ph-arrow-left ${styles.arrowOnlyIcon}`} aria-hidden="true" />
        ) : (
          <>
            <i className={`ph-fill ph-hand-pointing ${styles.icon}`} aria-hidden="true" />
            <span className={styles.text}>{text}</span>
            <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss hint">
              <i className="ph-bold ph-x" aria-hidden="true" />
            </button>
          </>
        )}
      </div>
      {arrows && layout?.visible && layout.arrows.length > 0 && (
        <svg
          className={`${styles.arrows} ${inScroller ? styles.inScroller : styles.inViewport}`}
          width={layout.canvas.width}
          height={layout.canvas.height}
          aria-hidden="true"
        >
          <defs>
            <marker id="coachArrowHead" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto" markerUnits="userSpaceOnUse">
              <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--brandcolor)" />
            </marker>
          </defs>
          {layout.arrows.map((a, i) => (
            <path key={i} d={curvedPath(a.from, a.to)} className={styles.arrowPath} markerEnd="url(#coachArrowHead)" />
          ))}
        </svg>
      )}
    </>
  );

  return createPortal(node, host);
}
